'use client'

import { useState, useEffect } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'
import { 
  BarChart2,
  Clock, 
  FileText, 
  LayoutGrid, 
  LogOut, 
  Settings, 
  Shield,
  Newspaper,
  PlusCircle,
  Bookmark,
  Search,
  Tags,
  Bug,
  Home,
  Users,
  ShieldCheck,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/utils/supabase'
import clsx from 'clsx'
import { Logo } from '@/components/logo'

// Helper function to check if a path is active
const isActivePath = (current: string, href: string): boolean => {
  if (href === '/') {
    return current === href;
  }
  return current.startsWith(href);
};

// Navigation item type definition
type NavItem = {
  name: string
  href: string
  icon: React.ElementType
  alwaysShow?: boolean;
  checkAccess?: () => boolean;
}

// Define props for Sidebar
// interface SidebarProps {
//   mobileControlOpen?: boolean; // Prop to control expansion from parent (MainLayout)
// }

export default function Sidebar() {
  const pathname = usePathname()
  const router = useRouter()
  const { signOut, user, userAccess, canAccessPage, loading: authLoading } = useAuth()
  const [isExpanded, setIsExpanded] = useState(false)

  // Format the name based on user data
  const getUserDisplayName = () => {
    // Prioritize full_name from profile table
    if (userAccess?.full_name) {
      return userAccess.full_name;
    }
    
    // Fallback to user_metadata.full_name if available
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name;
    }
    
    // Further fallback to first part of email if needed
    if (user?.email) {
      const emailParts = user.email.split('@');
      return emailParts[0]; // Just use the username part
    }
    
    // Default if nothing else is available
    return 'User'; 
  }

  // Check if user has admin access
  const isAdmin = userAccess?.accessLevel?.name === 'Admin' || canAccessPage('/admin');

  // Define navigation items with potential access checks
  const mainItem: NavItem = { name: 'Main', href: '/', icon: Home, alwaysShow: true };
  const dashboardItem: NavItem = { name: 'Dashboard', href: '/dashboard', icon: LayoutGrid };
  const mediaClipsItem: NavItem = { name: 'Media Clips', href: '/media-clips', icon: Newspaper };
  const mediaOutletsItem: NavItem = { name: 'Media Outlets', href: '/media-outlets', icon: FileText };
  const brandsItem: NavItem = { name: 'Brands', href: '/brands', icon: Tags };
  const brandQueryItem: NavItem = { name: 'Brand Query', href: '/brand-query', icon: Search };
  const settingsItem: NavItem = { name: 'Settings', href: '/settings', icon: Settings };
  const debugItem: NavItem = { name: 'Debug', href: '/debug', icon: Bug, checkAccess: () => {
      // Check for both development environment AND Admin role
      const isAdminUser = userAccess?.accessLevel?.name === 'Admin';
      return process.env.NODE_ENV === 'development' && isAdminUser;
    } 
  }; 

  // Admin items - access check uses canAccessPage('/admin') or specific sub-paths
  const accessLevelsItem: NavItem = { name: 'Access Levels', href: '/admin/access-levels', icon: ShieldCheck };
  const userManagementItem: NavItem = { name: 'User Management', href: '/admin/users', icon: Users };

  // Group navigation items
  const mainNavItems: NavItem[] = [dashboardItem];
  const corporateBrandNavItems: NavItem[] = [mediaClipsItem, mediaOutletsItem, brandsItem, brandQueryItem];
  const personalBrandNavItems: NavItem[] = [];
  const accountNavItems: NavItem[] = [settingsItem, debugItem];
  const adminNavItems: NavItem[] = [accessLevelsItem, userManagementItem];

  // Use router.push for client-side navigation
  const navigateTo = (e: React.MouseEvent<HTMLAnchorElement>, href: string): void => {
    e.preventDefault();
    router.push(href);
  };

  const handleLogout = async () => {
    try {
      // Clear auth state manually for redundancy
      if (typeof window !== 'undefined') {
        // Clear localStorage
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('auth_in_progress');
        localStorage.removeItem('auth_user_id');
        localStorage.removeItem('auth_session_active');
        localStorage.removeItem('force_auth_success');
        localStorage.removeItem('supabase.auth.token');
        
        // Clear sessionStorage
        sessionStorage.removeItem('force_dashboard_view');
        sessionStorage.removeItem('auth_bypass');
        sessionStorage.removeItem('auth_redirecting');
      }
      
      // Call the context's signOut function
      await signOut();
      
      // Fallback redirect in case the context signOut doesn't redirect
      window.location.href = '/';
    } catch (error) {
      // Force redirect to home even if logout fails
      window.location.href = '/';
    }
  }

  // Helper function to render a single navigation item if allowed
  const renderNavItem = (item: NavItem) => {
    // Use specific checkAccess function if provided, otherwise use canAccessPage
    const hasAccess = item.alwaysShow || (item.checkAccess ? item.checkAccess() : canAccessPage(item.href));

    if (!hasAccess) {
      return null; // Don't render the item if user lacks access
    }

    const Icon = item.icon;
    const isActive = isActivePath(pathname, item.href);

    return (
      <div key={item.name} className="mb-1">
        <a
          href={item.href}
          onClick={(e) => navigateTo(e, item.href)}
          className={clsx(
            'group flex items-center rounded-lg px-3 py-2 text-sm font-medium cursor-pointer transition-colors duration-150',
            {
              'bg-[#8A6FE8]/10 text-[#8A6FE8]': isActive,
              'text-gray-300 hover:bg-gray-700/50': !isActive,
              'justify-center': !isExpanded
            }
          )}
        >
          <Icon 
            className={clsx(
              'h-5 w-5 shrink-0',
              {
                'text-[#8A6FE8]': isActive,
                'text-gray-400': !isActive,
                'mr-3': isExpanded
              }
            )} 
          />
          {isExpanded && <span className="flex-1 truncate">{item.name}</span>}
        </a>
      </div>
    );
  };

  return (
    <div className={clsx(
      "flex h-screen flex-col border-r border-gray-800 bg-[#141724] shadow-md transition-all duration-300",
      isExpanded ? "w-[250px]" : "w-[70px]"
    )}>
      {/* Logo section & Toggle Button */}
      <div className={clsx(
        "flex h-[60px] items-center border-b border-gray-800",
        isExpanded ? "px-4 justify-between" : "justify-center px-2"
      )}>
        {/* Logo Link */}
        <Link 
          href="/" 
          className={clsx(
            "flex items-center gap-2",
            !isExpanded && "hidden"
          )}
          onClick={(e) => navigateTo(e, '/')}
        >
          <Logo className="h-6 w-6 shrink-0" />
          <span className="text-xl font-semibold text-white">
            m
            <span
              style={{
                // Apply a gradient background
                background: 'linear-gradient(to right, #A855F7, #EC4899)', // Example: purple-500 to pink-500
                // Clip the background to the text shape
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                // Make the text color transparent so the background shows through
                WebkitTextFillColor: 'transparent',
                // Optional: Standard property, less critical than -webkit prefix
                // textFillColor: 'transparent' 
              }}
            >
              ai
            </span>
            d.
          </span>
        </Link>

        {/* Toggle Button - Now always visible, but only functional on md+ via layout */}
        <button 
          onClick={() => setIsExpanded(!isExpanded)} 
          className="inline-flex rounded-md p-2 text-gray-300 hover:bg-gray-700/50 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#8A6FE8]"
          aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
        >
          {isExpanded ? 
            <ChevronLeft className="h-6 w-6" /> :
            <ChevronRight className="h-6 w-6" />
          }
        </button>
      </div>

      {/* Navigation section */}
      <nav className={clsx(
        "flex-1 overflow-y-auto py-6",
        isExpanded ? "px-4" : "px-2"
      )}>
        <div className="space-y-1">
          {/* Main Menu Section */}
          {isExpanded && (
            <div className="px-2 py-2 text-xs font-semibold uppercase text-gray-400">
              Main Menu
            </div>
          )}
          {mainNavItems.map(renderNavItem)}

          {/* Separator - Only show when collapsed */}
          {!isExpanded && <hr className="my-2 border-t border-gray-700 mx-2" />}

          {/* Corporate Brand Section */}
          {isExpanded && (
            <div className="mt-4 px-2 py-2 text-xs font-semibold uppercase text-gray-400">
              Media
            </div>
          )}
          {corporateBrandNavItems.map(renderNavItem)}

          {/* Admin Section - Render items individually based on access */}
          {adminNavItems.some(item => canAccessPage(item.href)) && (
             <>
               {isExpanded && (
                 <div className="mt-4 px-2 py-2 text-xs font-semibold uppercase text-gray-400">
                   Admin
                 </div>
               )}
               {adminNavItems.map(renderNavItem)}
             </>
          )}

          {/* Account Section */}
          {isExpanded && (
            <div className="mt-4 px-2 py-2 text-xs font-semibold uppercase text-gray-400">
              Account
            </div>
          )}
          {accountNavItems.map(renderNavItem)}
        </div>
      </nav>

      {/* User section */}
      <div className="border-t border-gray-800 py-4 px-4">
        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className={clsx(
            "group mt-2 flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-gray-300 hover:bg-red-800/30 hover:text-red-400",
            !isExpanded && "justify-center"
          )}
        >
          <LogOut className={clsx(
            "h-5 w-5 shrink-0 text-gray-400 group-hover:text-red-400",
            isExpanded && "mr-3"
          )} />
          {isExpanded && <span className="flex-1">Logout</span>}
        </button>
      </div>
    </div>
  );
} 